import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {CalculationInputField} from './calculation-input-field';
import {FormControl, FormGroup} from '@angular/forms';
import {CalculationService} from '../../services/calculation/calculation.service';
import {CalculationField} from '../../interfaces/CalculationField';
import {EFieldType} from '../../enums/e-field-type.enum';
import {Field} from '../../interfaces/Field';
import {FieldSelectorDialogComponent} from '../field-selector-dialog/field-selector-dialog.component';
import {MdDialog} from '@angular/material';
import {FieldGroup} from '../../interfaces/FieldGroup';
import {StringUtilities} from '../../../utilities/StringUtilities';
import {FieldGroupService} from '../../services/field-group/field-group.service';
import {FieldValue} from '../../../form-renderer/interfaces/FieldValue';
import {ECalculationStatus} from './e-calculation-status';
import {CalculationResult} from '../../services/calculation/calculation-result.interface';

@Component({
  selector: 'app-calculation-editor',
  templateUrl: './calculation-editor.component.html',
  styleUrls: ['./calculation-editor.component.scss']
})
export class CalculationEditorComponent implements OnInit {

  @Input() fieldGroupId: number;
  @Input() field: Field;
  @Output() onCalculationStatusChanged = new EventEmitter();
  type: number;
  calculationFormGroup = new FormGroup({});
  calculationFields: CalculationInputField[] = [];
  values: {field: Partial<FieldValue>}[] = [];
  formula: string;
  lettersModel: string[] = [];
  caretPos = 0;
  result = '';
  public status: ECalculationStatus = ECalculationStatus.NOT_RUN;
  // bind the enum to the template
  ECalculationStatus = ECalculationStatus;

  lettersArray: string[] = [
    'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L',
    'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X',
    'Y', 'Z'
  ];

  constructor( private calculationService: CalculationService,
               public dialog: MdDialog,
               private fieldGroupService: FieldGroupService) { }

  async ngOnInit() {
    if (this.field.id !== undefined) {
      const calculationFields: CalculationField[] = await this.calculationService.getFields(this.field.id);
      await this.populateCalculation(calculationFields, this.field.calculation);
    }
  }

  get textTooltipText() {
    return this.canCalculate() ? 'Test the calculation to make sure it works.' :
      'You must fill in the example values and letters before you can test the calculation.';
  }

  defaultCalculationValues() {
    for (const calculationField of this.calculationFields) {
      if (!this.values[calculationField.field.id]) {
        this.values[calculationField.field.id] = { field: {} };
        const field = this.values[calculationField.field.id].field;
        switch (calculationField.field.type) {
          case EFieldType.INTEGER:
            field.int_value = 0;
            break;
          case EFieldType.DATE:
            field.date_value = '01/01/2000';
            break;
          case EFieldType.TIMESTAMP:
            field.timestamp_value = '00:00';
            break;
          case EFieldType.TEXT:
            field.string_value = 'TEST';
            break;
          case EFieldType.YES_NO:
            field.yes_no_value = true;
            break;
          case EFieldType.CHECKBOX:
            field.check_value = true;
            break;
          case EFieldType.DECIMAL:
            field.decimal_value = 0.00;
            break;
        }
      }
    }
  }

  /**
   * Select a new field to replace the given field
   * @param {CalculationInputField} fieldToReplace The field that has been clicked on
   */
  replaceCalculationInputField(fieldToReplace: CalculationInputField) {
    this.dialog
      .open(FieldSelectorDialogComponent, {data: {fieldGroup : fieldToReplace.fieldGroup, parent: this.fieldGroupId }})
      .afterClosed().subscribe(result => {
      if (result !== 'cancel' && result !== null && result !== undefined) {
        // We replace the field on the array for the new field, keeping the same position to keep track of the letters
        const index: number = this.calculationFields.indexOf(fieldToReplace);
        const field = {...result['field'], type: EFieldType[result['field'].type]};
        this.calculationFields[index] = {
          field: field,
          fieldGroup: result['fieldGroup'],
          type: result['type'],
          letter: fieldToReplace.letter
        };
      }});
  }

  addCalculationInputField() {
    this.dialog
      .open(FieldSelectorDialogComponent, {data: {parent: this.fieldGroupId }})
      .afterClosed()
      .subscribe(result => {
        if (result) {
          const field: Field = {...result['field'], type: result['field'].type};
          this.calculationFormGroup.addControl(StringUtilities.camelize(field.name), new FormControl(null));
          this.calculationFields.push({field: field, fieldGroup: result['fieldGroup'], type: result['type']});
        }
    });
  }

  setCaretPosition(inputElement: HTMLInputElement) {
    this.caretPos = inputElement.selectionStart;
  }

  getFieldValueFromField(field: Field) {
    return <FieldValue>{field: field};
  }

  /**
   * Builds the fields on the form to correspond with the calculation saved in the database
   * @param calculationFields
   * @param calculation
   */
  async populateCalculation(calculationFields: CalculationField[], calculation: string) {
    for (const calculationField of calculationFields) {
      const fieldGroup: FieldGroup = await this.fieldGroupService.getFieldGroup(calculationField.field_group_id);
      // When we grab the fields associate with the field group we run through them until we find the correct one
      const fields = fieldGroup.fields;
      const field = fields.find((f) => f.id === calculationField.source_field_id);
      if (!field)
        continue;
      // When we have the correct one we parse its fields and add it to the array of fields, making note of its index
      this.calculationFormGroup.addControl(StringUtilities.camelize(field.name), new FormControl(null));
      const rowIndex = this.calculationFields.push({
        field: field,
        fieldGroup: calculationField.field_group_id,
        type: calculationField.type,
        letter: calculationField.letter
      }) - 1;
      // With that index we put the identifier (letter) in the model for the dropdown
      this.lettersModel[rowIndex] = calculationField.letter;
    }
    this.formula = calculation;
  }

  /**
   * To calculate the output we first make sure that all fields have both a letter assigned and a number assigned
   * @returns {boolean}
   */
  public canCalculate() {
    for (const item of this.calculationFields) {
      if (item.letter == null)
        return false;
      if (!(item.field.id in this.values))
        return false;
    }
    return true;
  }

  /**
   * Calculates the example value of the calculation
   */
  runCalculation() {
    this.setStatus(ECalculationStatus.WAITING);

    if (!this.canCalculate())
      return;

    const calculationVariables = this.calculationFields.map((calculationField) => ({
      key: calculationField.letter,
      value: this.getFieldValue(calculationField.field.type, this.values[calculationField.field.id].field)
    }));

    const type = this.calculationFields[this.calculationFields.length - 1].type;

    this.calculationService.runCalculate(this.formula, calculationVariables, type)
      .then((result: CalculationResult) => {
        this.result = result.result;
        this.type = result.type;
        this.setStatus(ECalculationStatus.SUCCESS);
      })
      .catch(() => this.setStatus(ECalculationStatus.Failure));
  }

  setStatus(status: ECalculationStatus) {
    this.status = status;
    this.onCalculationStatusChanged.emit(this.status);
  }

  getFieldValue(type: number, field: Partial<FieldValue>) {
    switch (type) {
      case 1:
        return field.check_value;
      case 2:
        return field.string_value;
      case 3:
        return field.int_value;
      case 4:
        return field.decimal_value;
      case 5:
        return field.select_value;
      case 6:
        return field.timestamp_value;
      case 7:
        return field.date_value;
      case 8:
        return field.yes_no_value;
    }
  }

  removeField(field) {
    const index = this.calculationFields.indexOf(field);
    this.calculationFields.splice(index, 1);
    this.values.splice(field.field.id, 1);
    this.lettersModel.splice(index, 1);
  }

  public handleSubmit(formGroup: FormGroup) {
    this.defaultCalculationValues();
    this.runCalculation();
    formGroup.addControl('calculationFields', new FormControl(this.calculationFields));
    formGroup.addControl('calculation', new FormControl(this.formula));
    formGroup.addControl('calculation_type', new FormControl(this.type));
  }

  /**
   * When an option is selected in the dropdown for a field
   * @param {string} option The letter selected in the dropdown
   * @param {number} index of the row affected
   */
  optionSelected(option: string, index: number) {
    this.calculationFields[index].letter = option;
  }

  /**
   * This handles the processing of when the value of a field is changed
   * @param value The new value of the field that fired this method
   * @param {Field} field The field that fired this method
   */
  onValueChanged(value, field: Field) {
    this.values[field.id] = value;
  }

}



// WEBPACK FOOTER //
// /var/www/dnata-cerf-front-end/releases/20200326161233/src/app/modules/form-builder/components/calculation-editor/calculation-editor.component.ts